// Admin review moderation — /app/admin/reviews. Super admin sees all reviews;
// an org admin sees their own org's. Approve/hide low-star reviews before they
// hit the public homepage, and feature the best ones.
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/pages/dashboard/_shared";
import { Button } from "@/components/ui/button";
import { Star, Loader2, Eye, EyeOff, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "published", label: "Published" },
  { id: "hidden", label: "Hidden" },
];
const badge = { published: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", hidden: "bg-zinc-200 text-zinc-600" };

export default function AdminReviews() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/reviews/admin", { params: { status: tab } }); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === "super_admin") load(); }, [tab]); // eslint-disable-line

  if (user && user.role !== "super_admin") return <Navigate to="/app" replace />;

  const moderate = async (id, patch) => {
    try { await api.post(`/reviews/${id}/moderate`, patch); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const counts = data?.counts || {};
  return (
    <div className="pb-10" data-testid="admin-reviews-page">
      <PageHeader title="Reviews" subtitle="Approve, hide and feature customer reviews before they appear on the website." />
      <div className="flex gap-1 border-b border-zinc-200 mb-4">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-reviews-tab-${t.id}`}
            className={`px-3 py-2 text-sm font-bold border-b-2 -mb-px ${tab === t.id ? "border-[#FF5F15] text-[#FF5F15]" : "border-transparent text-zinc-500"}`}>
            {t.label}{t.id !== "all" && counts[t.id] != null ? ` (${counts[t.id]})` : ""}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>
      ) : (data?.reviews || []).length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">No reviews here.</div>
      ) : (
        <div className="space-y-3">
          {data.reviews.map((r) => (
            <div key={r.id} className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={`admin-review-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex">{[1,2,3,4,5].map((i)=><Star key={i} className={`w-4 h-4 ${i<=r.rating?"fill-[#FF5F15] text-[#FF5F15]":"text-zinc-300"}`} />)}</div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${badge[r.status]}`}>{r.status}</span>
                    {r.featured && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#FF5F15]/10 text-[#FF5F15]">Featured</span>}
                  </div>
                  {r.title && <div className="mt-2 font-bold">{r.title}</div>}
                  <p className="mt-1 text-sm text-zinc-700">{r.body}</p>
                  <div className="mt-2 text-xs text-zinc-400">{r.user_name} · {r.org_name} · shown as "{r.display_name}"</div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {r.status !== "published" && <Button size="sm" onClick={() => moderate(r.id, { status: "published" })} className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold" data-testid={`admin-publish-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Publish</Button>}
                  {r.status !== "hidden" && <Button size="sm" variant="outline" onClick={() => moderate(r.id, { status: "hidden" })} className="h-8 rounded-lg text-[11px] font-bold" data-testid={`admin-hide-${r.id}`}><EyeOff className="w-3.5 h-3.5 mr-1" /> Hide</Button>}
                  <Button size="sm" variant="outline" onClick={() => moderate(r.id, { featured: !r.featured })} className="h-8 rounded-lg text-[11px] font-bold"><Sparkles className="w-3.5 h-3.5 mr-1" /> {r.featured ? "Unfeature" : "Feature"}</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
